import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {
  ConnectionStatus,
  InstructionVideo,
  MediaDevice,
  MediaPlayback,
  MediaState,
  MusicPlayback,
  NetworkQualityPayload,
  SessionCapabilities,
  SessionParticipant,
  TimerPayload,
  UserIdType,
  ViewMode,
  ZoomOptions
} from './SessionState';
import {SessionChatMessage} from './SessionChatMessage';
import {SessionCommand} from './SessionCommand';
import {CpuPressureLevel} from './PerformanceMonitor';

/**
 * Immutable snapshot of the entire session state.
 * All arrays are readonly to prevent accidental mutations.
 */
export interface SessionSnapshot {
  readonly participants: readonly SessionParticipant[];
  readonly participantsRendered: readonly SessionParticipant[];
  readonly connectionStatus: ConnectionStatus | null;
  readonly connectionReason?: string;
  readonly localUser: {
    readonly id: number | null;
    readonly camera: MediaDevice | null;
    readonly microphone: MediaDevice | null;
    readonly speaker: MediaDevice | null;
    readonly isMuted: boolean;
    readonly isVideoOn: boolean;
    readonly isAudioOn: boolean;
    readonly mutedByHost: boolean;
    readonly unmutedByHost: boolean;
  };
  readonly devices: {
    readonly cameras: readonly MediaDevice[];
    readonly microphones: readonly MediaDevice[];
    readonly speakers: readonly MediaDevice[];
    readonly defaultCamera: MediaDevice | null;
    readonly defaultMicrophone: MediaDevice | null;
    readonly defaultSpeaker: MediaDevice | null;
  };
  readonly session: {
    readonly isJoined: boolean;
    readonly isEnded: boolean;
    readonly error: string | null;
    readonly capabilities: SessionCapabilities | null;
    readonly audioOnlyMode: boolean;
    readonly commandChannelConnected: boolean;
    readonly cpuPressure: CpuPressureLevel;
    readonly mediaState: MediaState | null;
  };
  readonly media: {
    readonly instructionVideo: InstructionVideo | null;
    readonly mediaPlayback: MediaPlayback | null;
    readonly musicPlayback: MusicPlayback | null;
    readonly musicVolume: number;
  };
  readonly ui: {
    readonly viewMode: ViewMode | null;
    readonly zoom: ZoomOptions;
    readonly activeSpeakers: readonly UserIdType[];
    readonly audioVolume: {userId: UserIdType; volume: number} | null;
    readonly chatMessage: SessionChatMessage | null;
    readonly command: SessionCommand | null;
    readonly timer: TimerPayload | null;
    readonly isMirrored: boolean | null;
    readonly isBlur: boolean | null;
    readonly isMuteAll: boolean | null;
    readonly networkQuality: NetworkQualityPayload | null;
  };
}

const initialSnapshot: SessionSnapshot = {
  participants: [],
  participantsRendered: [],
  connectionStatus: null,
  localUser: {
    id: null,
    camera: null,
    microphone: null,
    speaker: null,
    isMuted: false,
    isVideoOn: false,
    isAudioOn: false,
    mutedByHost: false,
    unmutedByHost: false
  },
  devices: {
    cameras: [],
    microphones: [],
    speakers: [],
    defaultCamera: null,
    defaultMicrophone: null,
    defaultSpeaker: null
  },
  session: {
    isJoined: false,
    isEnded: false,
    error: null,
    capabilities: null,
    audioOnlyMode: false,
    commandChannelConnected: false,
    cpuPressure: 'normal',
    mediaState: null
  },
  media: {
    instructionVideo: null,
    mediaPlayback: null,
    musicPlayback: null,
    musicVolume: 0.5
  },
  ui: {
    viewMode: null,
    zoom: {enabled: false},
    activeSpeakers: [],
    audioVolume: null,
    chatMessage: null,
    command: null,
    timer: null,
    isMirrored: null,
    isBlur: null,
    isMuteAll: null,
    networkQuality: null
  }
};

/**
 * A change description for partial updates to the session state.
 * Uses deep partial to allow updating nested properties.
 */
export type SessionSnapshotUpdate = {
  [K in keyof SessionSnapshot]?: SessionSnapshot[K] extends readonly unknown[]
    ? readonly SessionSnapshot[K][number][]
    : SessionSnapshot[K] extends object
      ? Partial<SessionSnapshot[K]>
      : SessionSnapshot[K]
};

/**
 * SessionStore provides immutable, observable state management with memoized selectors.
 *
 * Key features:
 * - State is always immutable - updates create new references
 * - Selectors are memoized - only emit when values actually change
 * - Supports partial updates with type safety
 * - Compatible with OnPush change detection
 */
export class SessionStore {
  private _snapshot = new BehaviorSubject<SessionSnapshot>(initialSnapshot);

  readonly snapshot$: Observable<SessionSnapshot> = this._snapshot.asObservable();

  get snapshot(): SessionSnapshot {
    return this._snapshot.value;
  }

  /**
   * Update the state with a partial update object.
   * Performs a shallow merge at the top level, deep merge for nested objects.
   */
  update(update: SessionSnapshotUpdate): void {
    const current = this._snapshot.value;
    const next: SessionSnapshot = {
      ...current,
      ...update,
      localUser: update.localUser ? {...current.localUser, ...update.localUser} : current.localUser,
      devices: update.devices ? {...current.devices, ...update.devices} : current.devices,
      session: update.session ? {...current.session, ...update.session} : current.session,
      media: update.media ? {...current.media, ...update.media} : current.media,
      ui: update.ui ? {...current.ui, ...update.ui} : current.ui
    };
    this._snapshot.next(next);
  }

  /**
   * Replace the entire state (use sparingly - prefer update())
   */
  replace(snapshot: SessionSnapshot): void {
    this._snapshot.next(snapshot);
  }

  /**
   * Reset to initial state (useful for cleanup)
   */
  reset(): void {
    this._snapshot.next(initialSnapshot);
  }

  /**
   * Select a slice of state with automatic memoization.
   * Only emits when the selected value changes (using deep equality for objects).
   */
  select<T>(selector: (snapshot: SessionSnapshot) => T): Observable<T> {
    return this._snapshot.pipe(
      map(selector),
      distinctUntilChanged((a, b) => this.deepEqual(a, b))
    );
  }

  /**
   * Select a slice of state synchronously
   */
  selectSync<T>(selector: (snapshot: SessionSnapshot) => T): T {
    return selector(this._snapshot.value);
  }

  /**
   * Convenience selectors for common state slices
   */
  readonly participants$ = this.select(s => s.participants);
  readonly connectionStatus$ = this.select(s => s.connectionStatus);
  readonly localUser$ = this.select(s => s.localUser);
  readonly devices$ = this.select(s => s.devices);
  readonly sessionError$ = this.select(s => s.session.error);
  readonly isJoined$ = this.select(s => s.session.isJoined);
  readonly activeSpeakers$ = this.select(s => s.ui.activeSpeakers);
  readonly viewMode$ = this.select(s => s.ui.viewMode);
  readonly chatMessage$ = this.select(s => s.ui.chatMessage);
  readonly command$ = this.select(s => s.ui.command);
  readonly timer$ = this.select(s => s.ui.timer);
  readonly zoom$ = this.select(s => s.ui.zoom);
  readonly error$ = this.select(s => s.session.error);
  readonly capabilities$ = this.select(s => s.session.capabilities);
  readonly musicPlayback$ = this.select(s => s.media.musicPlayback);
  readonly musicVolume$ = this.select(s => s.media.musicVolume);
  readonly cpuPressure$ = this.select(s => s.session.cpuPressure);

  /**
   * Batch multiple updates into a single emission
   */
  batchUpdate(updates: SessionSnapshotUpdate[]): void {
    if (updates.length === 0) return;

    const current = this._snapshot.value;
    let next = current;

    for (const update of updates) {
      next = {
        ...next,
        ...update,
        localUser: update.localUser ? {...next.localUser, ...update.localUser} : next.localUser,
        devices: update.devices ? {...next.devices, ...update.devices} : next.devices,
        session: update.session ? {...next.session, ...update.session} : next.session,
        media: update.media ? {...next.media, ...update.media} : next.media,
        ui: update.ui ? {...next.ui, ...update.ui} : next.ui
      };
    }

    this._snapshot.next(next);
  }

  /**
   * Deep equality check for memoization
   */
  private deepEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (typeof a !== 'object' || typeof b !== 'object') return false;
    if (a === null || b === null) return false;

    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);

    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
      if (!bKeys.includes(key)) return false;
      if (!this.deepEqual(aObj[key], bObj[key])) return false;
    }

    return true;
  }
}

/**
 * Factory function to create a store instance
 */
export function createSessionStore(): SessionStore {
  return new SessionStore();
}
